import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useMeasureElement} from '../../../helpers/hooks/domHookHelpers';
import {InlineEditingInputDisplay} from './inlineEditingInputDisplay';

/*
 * Types.
 */

interface InlineEditingBooleanPopoverProps {
  onEditSave: HandlerOf<string>;
  onEditCancel: Handler;
  value: string;
}

/*
 * Style.
 */

const StyledInlineEditingInputDisplay = styled(InlineEditingInputDisplay)`
  margin: 3px 0 2px 0;
`;

const StyledPopoverCoordinator = styled(PopoverCoordinator)`
  width: 100%;
`;

const StyledValueButtonDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${(p) => p.theme.greys.shade20};
  color: ${(p) => p.theme.greys.shade90};
  border-radius: 4px;
  padding: 5px 8px;
`;

/*
 * Component.
 */

export const InlineEditingBooleanPopover: FC<InlineEditingBooleanPopoverProps> = (props) => {
  const theme = useUserTheme();
  const {onEditSave, onEditCancel, value} = props;

  const [popoverButtonRef, {width: popoverButtonWidth}] = useMeasureElement();

  return (
    <StyledInlineEditingInputDisplay>
      <StyledPopoverCoordinator
        renderButton={() => (
          <StyledValueButtonDiv ref={popoverButtonRef}>
            {renderBooleanValue(value)}
            <Icon name="chevronDownSmall" color={theme.greys.shade70} />
          </StyledValueButtonDiv>
        )}
        renderModal={(popoverProps) => (
          <Dropdown {...popoverProps} width={popoverButtonWidth}>
            <DropdownList>
              <DropdownItem
                key="true"
                onSelect={() => {
                  onEditSave('true');
                }}
                isChecked={isTrue(value)}
              >
                <DropdownItemContent>{renderBooleanValue('true')}</DropdownItemContent>
              </DropdownItem>
              <DropdownItem
                key="false"
                onSelect={() => {
                  onEditSave('false');
                }}
                isChecked={isFalse(value)}
              >
                <DropdownItemContent>{renderBooleanValue('false')}</DropdownItemContent>
              </DropdownItem>
            </DropdownList>
          </Dropdown>
        )}
        onPopoverToggle={(isPopoverOpen) => {
          // User clicks out without selecting an option
          if (!isPopoverOpen) {
            onEditCancel();
          }
        }}
        isModalInitiallyOpen
      />
    </StyledInlineEditingInputDisplay>
  );
};

/*
 * Helpers.
 */

function renderBooleanValue(value: string) {
  if (isTrue(value)) {
    return <Trans id="contact-details-custom-attribute-value-yes">Yes</Trans>;
  }

  if (isFalse(value)) {
    return <Trans id="contact-details-custom-attribute-value-no">No</Trans>;
  }

  return null;
}

function isTrue(value: string) {
  return value === 'true';
}

function isFalse(value: string) {
  return value === 'false';
}
